
import { ReactNode, useEffect, useState, useRef } from "react";
import { useAuth } from "./useAuth";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { LoadingState } from "../admin/LoadingState";
import { toast } from "sonner";

export const RequireAuth = ({ 
  children, 
  adminOnly = false,
  superAdminOnly = false
}: { 
  children: ReactNode;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}) => {
  const { user, loading, profile, profileLoaded } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [startTime] = useState(Date.now());
  const [forceRedirectAttempted, setForceRedirectAttempted] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const emergencyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Enhanced loading timeout with multiple stages
  useEffect(() => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (emergencyTimeoutRef.current) {
      clearTimeout(emergencyTimeoutRef.current);
    }

    if (loading && !loadingTimedOut) {
      // First timeout at 8 seconds - show "taking longer" message
      timeoutRef.current = setTimeout(() => {
        console.log('RequireAuth: Loading timed out after 8 seconds');
        setLoadingTimedOut(true);
      }, 8000);

      // Emergency timeout at 15 seconds - force action
      emergencyTimeoutRef.current = setTimeout(() => {
        console.log('RequireAuth: Emergency timeout after 15 seconds');
        if (!user && !forceRedirectAttempted) {
          console.log('RequireAuth: Forcing redirect to auth due to emergency timeout');
          setForceRedirectAttempted(true);
          setRedirecting(true);
          navigate('/auth', { 
            state: { returnTo: location.pathname },
            replace: true
          });
        }
      }, 15000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (emergencyTimeoutRef.current) {
        clearTimeout(emergencyTimeoutRef.current);
      }
    };
  }, [loading, loadingTimedOut, user, forceRedirectAttempted, navigate, location.pathname]);

  // Handle authentication redirect
  useEffect(() => {
    if (!loading && !user && !location.pathname.includes('/auth') && !redirecting) {
      console.log('RequireAuth: User not authenticated, redirecting to auth');
      setRedirecting(true);
      
      const timer = setTimeout(() => {
        navigate('/auth', { 
          state: { returnTo: location.pathname },
          replace: true 
        });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [loading, user, location.pathname, navigate, redirecting]);

  // Handle admin access checks with better error handling
  useEffect(() => {
    if (!loading && user && (adminOnly || superAdminOnly)) {
      console.log('RequireAuth: Checking admin access', { 
        hasUser: !!user, 
        hasProfile: !!profile, 
        userRole: profile?.role,
        profileLoaded,
        adminOnly,
        superAdminOnly
      });
      
      if (!profileLoaded) {
        console.log('RequireAuth: Profile still loading for admin check');
        return;
      }
      
      if (!profile) {
        console.log('RequireAuth: No profile found for admin user');
        toast.error('Unable to verify admin access - profile not found');
        return;
      }
      
      if (superAdminOnly && profile.role !== 'super-admin') {
        console.log('RequireAuth: User is not a super-admin', { userRole: profile.role });
        toast.error('Super-admin access required');
        return;
      }
      
      if (adminOnly && !['admin', 'super-admin'].includes(profile.role || '')) {
        console.log('RequireAuth: User is not an admin', { userRole: profile.role });
        toast.error('Admin access required');
        return;
      }
    }
  }, [loading, user, profile, profileLoaded, adminOnly, superAdminOnly]);

  // Debug logging with throttling
  useEffect(() => {
    const elapsedTime = Date.now() - startTime;
    if (elapsedTime % 2000 < 100) { // Log every 2 seconds, not constantly
      console.log('RequireAuth state:', { 
        loading, 
        loadingTimedOut,
        user: user ? user.id : null, 
        profile: profile ? profile.id : null, 
        profileLoaded,
        redirecting,
        elapsedTime: `${elapsedTime}ms`,
        pathname: location.pathname
      });
    }
  }, [loading, loadingTimedOut, user, profile, profileLoaded, redirecting, startTime, location.pathname]);

  // Skip auth check for auth page
  if (location.pathname.includes('/auth')) {
    return <>{children}</>;
  }

  // Show enhanced loading state with timeout message
  if (loading && !loadingTimedOut) {
    return <LoadingState />;
  }

  // Show "taking longer" message after timeout
  if (loadingTimedOut && (loading || !user)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
        <LoadingState />
        <div className="text-center max-w-md">
          <p className="text-sm text-muted-foreground mb-2">
            Taking longer than expected...
          </p>
          <p className="text-xs text-muted-foreground">
            If this continues, try refreshing the page
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded text-sm hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Redirect to auth if no user
  if (!user) {
    console.log('RequireAuth: Redirecting to auth - no user found');
    return (
      <Navigate 
        to="/auth" 
        state={{ returnTo: location.pathname }} 
        replace 
      />
    );
  }

  // Handle admin checks
  if (adminOnly || superAdminOnly) {
    if (!profileLoaded) {
      console.log('RequireAuth: Profile still loading for admin check');
      return <LoadingState />;
    }
    
    if (!profile) {
      console.log('RequireAuth: No profile found for admin user');
      return <Navigate to="/" replace />;
    }
    
    if (superAdminOnly && profile.role !== 'super-admin') {
      console.log('RequireAuth: Access denied - not a super-admin');
      return <Navigate to="/" replace />;
    }
    
    if (adminOnly && !['admin', 'super-admin'].includes(profile.role)) {
      console.log('RequireAuth: Access denied - not an admin');
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};
